import { useMemo } from "react";

import { BarChart } from "@mantine/charts";
import { Box, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";


import { Select } from "components";

import { KpiYearlyResponse } from "types";

import classes from "./kpi.module.css";
import { getMetricLabelKey } from "./kpiCatalog";
import { getKpiStatusColor, KPI_TEXT_DARK, KPI_TEXT_GRAY } from "./kpiStatus";

const MONTH_LABEL_KEYS = [
  "profilePage.kpi.months.jan",
  "profilePage.kpi.months.feb",
  "profilePage.kpi.months.mar",
  "profilePage.kpi.months.apr",
  "profilePage.kpi.months.may",
  "profilePage.kpi.months.jun",
  "profilePage.kpi.months.jul",
  "profilePage.kpi.months.aug",
  "profilePage.kpi.months.sep",
  "profilePage.kpi.months.oct",
  "profilePage.kpi.months.nov",
  "profilePage.kpi.months.dec",
];

interface KpiYearlyOverviewProps {
  yearly: KpiYearlyResponse;
  year: number;
  onYearChange: (year: number) => void;
}

const KpiYearlyOverview = ({ yearly, year, onYearChange }: KpiYearlyOverviewProps) => {
  const { t } = useTranslation();

  // BE returns the per-month target line value already resolved.
  const monthlyTarget = yearly.monthlyTarget ?? null;

  // Only months with data are returned (spec). BE month is 1-12.
  const data = useMemo(
    () =>
      (yearly.months ?? []).map((point) => ({
        month: t(MONTH_LABEL_KEYS[Math.min(Math.max(point.month - 1, 0), 11)]),
        value: point.value,
      })),
    [yearly.months, t],
  );

  const yearOptions = useMemo(() => {
    const years = [year - 2, year - 1, year, year + 1];
    return years.map((y) => ({ value: String(y), label: String(y) }));
  }, [year]);

  return (
    <Box className={classes.card}>
      <Group justify="space-between" align="center" mb="md">
        <Text className={classes.cardTitle}>{t("profilePage.kpi.yearly.title")}</Text>
        <Select
          className={classes.yearSelect}
          data={yearOptions}
          value={String(year)}
          onChange={(value) => value && onYearChange(Number(value))}
          allowDeselect={false}
          mb={0}
        />
      </Group>

      {data.length === 0 ? (
        <Text size="sm" c={KPI_TEXT_GRAY}>
          {t("profilePage.kpi.emptyState")}
        </Text>
      ) : (
        <BarChart
          h={260}
          data={data}
          dataKey="month"
          series={[{ name: "value", label: t(getMetricLabelKey(yearly.metric)), color: KPI_TEXT_DARK }]}
          getBarColor={(value) =>
            monthlyTarget === null ? KPI_TEXT_GRAY : getKpiStatusColor(value, monthlyTarget)
          }
          referenceLines={
            monthlyTarget === null
              ? []
              : [
                  {
                    y: monthlyTarget,
                    color: KPI_TEXT_DARK,
                    label: t("profilePage.kpi.yearly.targetLine"),
                    labelPosition: "insideTopRight",
                  },
                ]
          }
          withLegend={false}
          // Dashed horizontal + vertical gridlines. The vertical lines are drawn at the
          // boundaries *between* category bands so they line up with the hover band edges.
          gridAxis="xy"
          strokeDasharray="4 4"
          gridProps={{
            stroke: "#D7DAE0",
            vertical: true,
            verticalCoordinatesGenerator: ({ offset }) => {
              const count = data.length;
              const left = offset?.left ?? 0;
              const plotWidth = offset?.width ?? 0;
              if (!count || !plotWidth) {
                return [];
              }
              const band = plotWidth / count;
              // Lines at each band boundary (between bars), including the two edges.
              return Array.from({ length: count + 1 }, (_, i) => left + band * i);
            },
          }}
          barProps={{ radius: 4 }}
          maxBarWidth={96}
          // Minimal gap between each bar and its band boundary (wider bars).
          barChartProps={{ barCategoryGap: "6%" }}
        />
      )}
    </Box>
  );
};

export default KpiYearlyOverview;
